import { useEffect, useRef, useState } from "react"
import { inputCls } from "./FormComponents"

export type SearchMultiSelectOption = {
  id: string
  label: string
  sublabel?: string
}

export type SearchMultiSelectProps<O extends SearchMultiSelectOption> = {
  /** Currently selected options (small list — shown as removable chips). */
  selectedOptions: O[]
  onToggle: (option: O) => void
  /** Server-side search — called debounced, only once the user has typed something. Never called with an empty query. */
  onSearch: (query: string) => Promise<O[]>
  placeholder?: string
  emptyMessage?: string
  debounceMs?: number
}

/**
 * Typeahead multi-select backed by server-side search — never loads the full
 * candidate list into memory (the pool can be thousands of students or
 * seedbeds). The dropdown only appears once the user types; results are
 * fetched debounced per keystroke. Selected items become removable chips
 * above the search box. Generic over the option shape `O` so callers can
 * carry extra fields (e.g. institution) through `onToggle` without a lookup.
 * Used for adding students/seedbeds to a group (seedbed-group-manager).
 */
export function SearchMultiSelect<O extends SearchMultiSelectOption>({
  selectedOptions,
  onToggle,
  onSearch,
  placeholder = "Buscar...",
  emptyMessage = "Sin resultados.",
  debounceMs = 300,
}: SearchMultiSelectProps<O>) {
  const [query, setQuery] = useState("")
  const [isOpen, setIsOpen] = useState(false)
  const [loading, setLoading] = useState(false)
  const [results, setResults] = useState<O[]>([])
  const containerRef = useRef<HTMLDivElement>(null)
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const requestIdRef = useRef(0)

  useEffect(() => {
    function onClickOutside(e: MouseEvent) {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setIsOpen(false)
      }
    }
    document.addEventListener("mousedown", onClickOutside)
    return () => document.removeEventListener("mousedown", onClickOutside)
  }, [])

  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current)

    const q = query.trim()
    if (!q) {
      setResults([])
      setLoading(false)
      return
    }

    setLoading(true)
    const requestId = ++requestIdRef.current
    debounceRef.current = setTimeout(() => {
      onSearch(q)
        .then((options) => {
          if (requestIdRef.current !== requestId) return // stale response, a newer query superseded it
          setResults(options)
        })
        .catch(() => {
          if (requestIdRef.current === requestId) setResults([])
        })
        .finally(() => {
          if (requestIdRef.current === requestId) setLoading(false)
        })
    }, debounceMs)

    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- onSearch is expected to be stable per caller
  }, [query, debounceMs])

  const selectedIds = new Set(selectedOptions.map((o) => o.id))
  const visibleResults = results.filter((o) => !selectedIds.has(o.id))
  const showDropdown = isOpen && query.trim().length > 0

  return (
    <div ref={containerRef} className="relative">
      {selectedOptions.length > 0 && (
        <div className="mb-2 flex flex-wrap gap-1.5">
          {selectedOptions.map((o) => (
            <span key={o.id}
              className="flex items-center gap-1.5 rounded-full bg-brand-50 py-1 pl-3 pr-1.5 text-xs font-medium text-brand-700 dark:bg-brand-500/10 dark:text-brand-300">
              {o.label}
              <button type="button" onClick={() => onToggle(o)}
                className="flex h-4 w-4 items-center justify-center rounded-full text-brand-400 hover:bg-brand-100 hover:text-brand-700 dark:hover:bg-brand-500/20">
                ✕
              </button>
            </span>
          ))}
        </div>
      )}

      <input
        type="text"
        value={query}
        onChange={(e) => {
          setQuery(e.target.value)
          setIsOpen(true)
        }}
        onFocus={() => setIsOpen(true)}
        placeholder={placeholder}
        className={inputCls}
      />

      {showDropdown && (
        <div className="absolute z-20 mt-1 max-h-64 w-full overflow-y-auto rounded-xl border border-gray-200 bg-white py-1 shadow-theme-lg dark:border-gray-700 dark:bg-gray-900">
          {loading ? (
            <p className="px-3 py-2 text-xs text-gray-500 dark:text-gray-400">Buscando...</p>
          ) : visibleResults.length === 0 ? (
            <p className="px-3 py-2 text-xs text-gray-500 dark:text-gray-400">{emptyMessage}</p>
          ) : (
            visibleResults.map((o) => (
              <button
                key={o.id}
                type="button"
                onClick={() => {
                  onToggle(o)
                  setQuery("")
                  setIsOpen(false)
                }}
                className="flex w-full flex-col items-start px-3 py-2 text-left text-sm hover:bg-gray-50 dark:hover:bg-gray-800"
              >
                <span className="text-gray-800 dark:text-gray-200">{o.label}</span>
                {o.sublabel && <span className="text-xs text-gray-500 dark:text-gray-400">{o.sublabel}</span>}
              </button>
            ))
          )}
        </div>
      )}
    </div>
  )
}

export default SearchMultiSelect
